import React, { useContext, useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, getBezierPath, getStraightPath, type EdgeProps } from 'reactflow';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LayoutDirectionContext } from './PrimitiveNode';

// Andie Drama session (2026-09-23) — a CONDITION's branch label is sometimes a full-sentence
// paraphrase ("Critical product issues, urgent troubleshooting needs, or severe complaints"),
// not just "Yes"/"No". BaseEdge's native label renders via SVG <text>/EdgeText, which has no
// wrap — multi-line would mean manual word-measuring/<tspan> splitting for a cosmetic fix.
// Converged instead on: truncate on a word boundary, full text on hover — matching the hover
// convention this same component already uses for its insert/delete icons, and cheap enough
// that the label can move to a real HTML element (this portal already renders one for those
// icons), getting a real MUI Tooltip for free instead of fighting SVG for one.
const EDGE_LABEL_MAX_CHARS = 24;

function truncateEdgeLabel(text: string): { display: string; truncated: boolean } {
  if (text.length <= EDGE_LABEL_MAX_CHARS) return { display: text, truncated: false };
  const cut = text.slice(0, EDGE_LABEL_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  const display = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd();
  return { display: `${display}…`, truncated: true };
}

export interface WorkflowEdgeData {
  condition?: string | null;
  /** NEW — computed in WorkflowCanvasTab's displayEdges memo from the full edges list. True
   * when this edge's source fans out to more than one edge, or its target merges more than one
   * edge in — forces a bezier curve even in horizontal layout, matching the reference flow
   * where only plain 1-to-1 links are dead straight. */
  isBranch?: boolean;
  /** NEW — opens the node picker to splice a new node into this specific edge. */
  onInsert?: (edgeId: string) => void;
  /** NEW — deletes this specific edge directly from the hover toolbar, without needing to
   * select it first the way the Inspector's "Delete edge" button requires. */
  onDelete?: (edgeId: string) => void;
  /** Optional override for the delete button's tooltip text — defaults to "Delete connection"
   * (WorkflowCanvasTab.tsx's legacy canvas relies on that default; it genuinely deletes an edge
   * there). RuleFlowCanvas.tsx overrides this to "Delete this step" since its own onDelete
   * deletes that edge's target NODE instead — set per-caller via `data`, never by editing this
   * shared component's own hardcoded string, so the legacy canvas is unaffected either way. */
  deleteTooltip?: string;
}

/**
 * Custom edge: same visual line React Flow's default edge draws (bezier for vertical layout,
 * straight for horizontal), plus a small Add/Delete button pair that appears at the edge's
 * midpoint on hover — matching n8n's inline "+"/trash affordance between connected nodes.
 * CSS-only `:hover` can't reach across the EdgeLabelRenderer portal boundary, so hover state is
 * tracked locally via an invisible wide interaction stroke instead.
 */
function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  label,
  labelStyle,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  data,
}: EdgeProps<WorkflowEdgeData>) {
  const [hovered, setHovered] = useState(false);
  // NEW — read the SAME context PrimitiveNode uses for its handle positions, rather than a
  // separate `straight` flag threaded through edge data. Guarantees the edge's curve and the
  // node's connector sides can never disagree with each other (which is what produced the
  // original "edges loop around node bodies" bug this whole direction-matching setup exists to
  // avoid — see computeAutoLayout's comment in WorkflowCanvasTab.tsx).
  const layoutDirection = useContext(LayoutDirectionContext);
  // A plain 1-to-1 link in horizontal layout is dead straight; any fan-out/fan-in point
  // (isBranch) always gets the softer bezier curve instead, in either layout direction.
  const useStraight = layoutDirection === 'horizontal' && !data?.isBranch;
  const [path, labelX, labelY] = useStraight
    ? getStraightPath({ sourceX, sourceY, targetX, targetY })
    : getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });

  return (
    <>
      {/* The condition label (below, in EdgeLabelRenderer) is rendered as real HTML now, not via
          BaseEdge's own label/labelX/labelY props — a full-sentence paraphrase condition needs a
          Tooltip for its truncated text, and BaseEdge's label renders through SVG <text>
          (EdgeText), which has no wrap and can't host a real MUI Tooltip. */}
      <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} />
      {/* Wide, invisible stroke — a much easier target than the thin visible line for
          triggering the hover toolbar below. Purely an interaction layer, no visual style. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      <EdgeLabelRenderer>
        {label != null && label !== '' && (() => {
          const { display, truncated } = truncateEdgeLabel(String(label));
          const chip = (
            <Box
              sx={{
                fontSize: labelStyle?.fontSize ?? 11,
                fontWeight: labelStyle?.fontWeight ?? 700,
                color: labelStyle?.fill ?? '#37474f',
                backgroundColor: labelBgStyle?.fill ?? '#ffffff',
                px: `${labelBgPadding?.[0] ?? 6}px`,
                py: `${labelBgPadding?.[1] ?? 3}px`,
                borderRadius: `${labelBgBorderRadius ?? 4}px`,
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
              }}
            >
              {display}
            </Box>
          );
          return (
            <Box
              sx={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                zIndex: 9,
                pointerEvents: 'all',
              }}
            >
              {truncated ? <Tooltip title={String(label)}>{chip}</Tooltip> : chip}
            </Box>
          );
        })()}
        <Box
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            position: 'absolute',
            // Nudged down when a label chip is present (below) so the two never sit exactly on
            // top of each other — the label is always visible, this row only on hover.
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY + (label ? 18 : 0)}px)`,
            display: 'flex',
            gap: 0.5,
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.15s',
            pointerEvents: hovered ? 'all' : 'none',
            zIndex: 10,
          }}
        >
          <Tooltip title="Insert step here">
            <IconButton
              size="small"
              onClick={(e) => {
                // Without this, the click bubbles up to ReactFlow's own edge-click handling,
                // which selects this edge and closes the picker right after this opens it —
                // that's what made the picker never actually show.
                e.stopPropagation();
                data?.onInsert?.(id);
              }}
              sx={{
                width: 22,
                height: 22,
                backgroundColor: 'primary.main',
                color: '#fff',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={data?.deleteTooltip ?? 'Delete connection'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                data?.onDelete?.(id);
              }}
              sx={{
                width: 22,
                height: 22,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.main', color: '#fff' },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </EdgeLabelRenderer>
    </>
  );
}

export default WorkflowEdge;
